import _ from "lodash"
import { useContext, useState, useEffect, useRef } from "react"
import { useQuery } from "@apollo/client"
import ProfilePhoto from "../ProfilePhoto"
import { LoaderProgressBar } from "../Loaders"
import { UserContext } from "../../context/userContext"
import {
  GET_PARTICIPANT,
  PARTICIPANT_BLOCK,
} from "../../pages/Participant/ParticipantProfile/queries"
import {
  clearCopyFeedbackTimer,
  copyWithFeedback,
} from "../../helpers/copyFeedback"
import styles from "../../styles/ParticipantProfileBoxSmall.module.css"

export default function ParticipantProfileBox() {
  const userCtx = useContext(UserContext)
  const [currentParticipant, setCurrentParticipant] = useState(null)
  const [contactInformation, setContactInformation] = useState({})
  const [labels, setLabels] = useState({})
  const [loadingLabels, setLoadingLabels] = useState(false)
  const lastParticipantContextRef = useRef(null)
  const lastProfileBoxRefreshVersionRef = useRef(
    userCtx.profileBoxRefreshVersion,
  )

  // Tracks which pill was copied to show short-lived visual confirmation.
  const [copiedPillKey, setCopiedPillKey] = useState(null)
  const copyResetTimerRef = useRef(null)

  // Fetch participant data via Apollo.
  const { loading: loadingParticipant, refetch } = useQuery(GET_PARTICIPANT, {
    variables: {
      participant_id: userCtx.current_participant_id,
      organization_id: userCtx.session.user.organization_id,
    },
    skip: _.isEmpty(userCtx.current_participant_id),
    fetchPolicy: "cache-and-network",
    onCompleted: ({ participant }) => {
      const cleanedParticipant = _.cloneDeepWith(participant, (value) => {
        if (typeof value === "string") {
          return _.unescape(value)
        }
        return undefined
      })

      // Some payloads send "0" for an otherwise empty zip code; normalize for display.
      if (cleanedParticipant?.participant_zip_code_primary === "0") {
        cleanedParticipant.participant_zip_code_primary = ""
      }

      setCurrentParticipant(cleanedParticipant)
    },
  })

  const { loading: loadingContact, refetch: refetchContact } = useQuery(
    PARTICIPANT_BLOCK,
    {
    variables: {
      participant_id: userCtx.current_participant_id,
      organization_id: userCtx.session.user.organization_id,
      enterprise_id: userCtx.session.user.enterprise_id,
      idBlock: "participant__contact_information",
    },
    skip: _.isEmpty(userCtx.current_participant_id),
    fetchPolicy: "cache-and-network",
    onCompleted: (data) => {
      const blockData = data?.blockSaved?.data
      if (!blockData) {
        setContactInformation({})
        return
      }

      try {
        const parsed = JSON.parse(blockData)
        setContactInformation(parsed?.values || {})
      } catch (err) {
        console.error("Error parsing contact information", err)
        setContactInformation({})
      }
    },
    onError: (err) => {
      console.error("Error fetching contact information", err)
      setContactInformation({})
    },
    },
  )

  useEffect(() => {
    const participantId = userCtx.current_participant_id || null
    const participantMembershipId = userCtx.participant_membership_id || null
    const hasProfileBoxRefreshChanged =
      lastProfileBoxRefreshVersionRef.current !==
      userCtx.profileBoxRefreshVersion
    const participantSignature = {
      participantId,
      participantMembershipId,
    }
    const noParticipantSelected = _.isEmpty(participantId)

    // If no participant is selected, clear the current participant context.
    if (noParticipantSelected) {
      lastParticipantContextRef.current = participantSignature
      setCurrentParticipant(null)
      return
    }

    // Compare against the previous participant context to avoid extra requests.
    const previousSignature = lastParticipantContextRef.current
    const hasContextChanged =
      previousSignature !== null &&
      (previousSignature.participantId !== participantId ||
        previousSignature.participantMembershipId !== participantMembershipId)

    // The profile card now stays mounted globally. Refetch whenever
    // participant context changes so we keep details in sync.
    if (hasContextChanged) {
      refetch({
        participant_id: participantId,
        organization_id: userCtx.session.user.organization_id,
      })
    }

    if (hasProfileBoxRefreshChanged) {
      refetch({
        participant_id: participantId,
        organization_id: userCtx.session.user.organization_id,
      })

      refetchContact({
        participant_id: participantId,
        organization_id: userCtx.session.user.organization_id,
        enterprise_id: userCtx.session.user.enterprise_id,
        idBlock: "participant__contact_information",
      })
    }

    lastParticipantContextRef.current = participantSignature
    lastProfileBoxRefreshVersionRef.current = userCtx.profileBoxRefreshVersion
  }, [
    refetch,
    refetchContact,
    userCtx.current_participant_id,
    userCtx.participant_membership_id,
    userCtx.profileBoxRefreshVersion,
    userCtx.session.user.organization_id,
    userCtx.session.user.enterprise_id,
  ])

  // Resolve the label for the participant's latest activity template.
  const idLastActivity = currentParticipant?.id_participant_last_activity

  useEffect(() => {
    // If there is no linked activity template, clear any previously loaded labels.
    if (!idLastActivity) {
      setLabels({})
      return
    }

    setLoadingLabels(true)
    const labelsRef = userCtx.session.labels

    // Subscribe to label changes from Firestore.
    const unsubscribe = labelsRef.onSnapshot(
      (docSnapshot) => {
        const labelList = docSnapshot.data()
        const reqLabels = _.reduce(
          [idLastActivity],
          (acc, template_name) => {
            const reqTemplate = _.find(labelList, { template_name })
            if (_.isEmpty(reqTemplate)) return acc

            const disabledTemplate = reqTemplate.template_availability === 0
            if (disabledTemplate) {
              return { ...acc, [template_name]: null }
            }

            return { ...acc, [template_name]: reqTemplate.template_title }
          },
          {},
        )

        setLabels(reqLabels)
        setLoadingLabels(false)
      },
      (err) => {
        console.error(`Error fetching labels: ${err}`)
        setLoadingLabels(false)
      },
    )

    // Stop listening when inputs change or the component unmounts.
    return () => unsubscribe()
  }, [idLastActivity, userCtx.session.labels])

  useEffect(() => {
    // Clear pending reset timer to avoid setting state after unmount.
    return () => {
      clearCopyFeedbackTimer(copyResetTimerRef)
    }
  }, [])

  // Unify query-loading and label-loading into one render gate.
  const isLoading = loadingParticipant || loadingContact || loadingLabels

  const displayValue = (value) => {
    if (_.isNil(value)) return "-"

    if (_.isString(value)) {
      const trimmedValue = value.trim()
      return trimmedValue.length ? trimmedValue : "-"
    }

    return value
  }

  if (_.isEmpty(userCtx.current_participant_id)) {
    return null
  }

  const hasParticipantData =
    !_.isEmpty(currentParticipant) && !_.isEmpty(contactInformation)
  const isRefreshing = isLoading && hasParticipantData

  if (isLoading && !hasParticipantData) {
    return null
  }

  const handleProgramList = _.join(currentParticipant?.program_list, ", ")

  const fullName = _.compact([
    currentParticipant?.participant_name_first,
    currentParticipant?.participant_name_last,
  ]).join(" ")

  const preferredParticipantName = _.isString(
    contactInformation?.participant_name_preferred,
  )
    ? contactInformation.participant_name_preferred.trim()
    : ""

  const preferredContact = _.isArray(
    contactInformation?.participant_preferred_contact,
  )
    ? contactInformation.participant_preferred_contact[0]
    : contactInformation?.participant_preferred_contact || ""

  const preferredContactValue =
    _.trim(preferredContact) === "Other"
      ? contactInformation?.participant_preferred_contact_other
      : preferredContact

  const emailValue = [
    contactInformation?.participant_email_primary ||
      currentParticipant?.participant_email_account,
    contactInformation?.participant_email_secondary ||
      currentParticipant?.participant_email_secondary,
  ]
    .map((value) => (_.isString(value) ? value.trim() : value))
    .filter(Boolean)
    .join(", ")

  const phoneValue = [
    contactInformation?.participant_phone_primary ||
      currentParticipant?.participant_phone_primary,
    contactInformation?.participant_phone_secondary ||
      currentParticipant?.participant_phone_secondary,
    ..._.map(
      contactInformation?.participant_secondary_phones_multiblock || [],
      "participant_phone_secondary",
    ),
  ]
    .map((value) => (_.isString(value) ? value.trim() : value))
    .filter(Boolean)
    .filter((value, index, values) => values.indexOf(value) === index)
    .join(", ")

  const emailPills = emailValue ? emailValue.split(", ").filter(Boolean) : []
  const phonePills = phoneValue ? phoneValue.split(", ").filter(Boolean) : []

  const lastActivity = displayValue(
    labels[idLastActivity] || currentParticipant?.participant_last_activity,
  )

  const lastEngagementDate = displayValue(
    currentParticipant?.participant_last_engagement,
  )

  const assignmentRows = [
    {
      label: "Assigned Staff",
      value: displayValue(currentParticipant?.staff_full_name),
    },
    {
      label: "Location",
      value: displayValue(currentParticipant?.locations),
    },
    {
      label: "Program",
      value: displayValue(handleProgramList),
    },
  ]

  const contactRows = [
    {
      label: "Contact Preference",
      value: displayValue(preferredContactValue),
    },
    {
      label: "Email",
      value: displayValue(emailValue),
      pills: emailPills,
    },
    {
      label: "Phone",
      value: displayValue(phoneValue),
      pills: phonePills,
    },
  ]

  // Pill config keeps rendering/behavior for IDs in one place.
  const idPills = [
    {
      key: "membershipId",
      label: "Membership ID",
      value: displayValue(currentParticipant?.participant_membership_id),
    },
    {
      key: "participantId",
      label: "Participant ID",
      value: displayValue(currentParticipant?.participant_alias),
    },
    {
      key: "externalId",
      label: "External ID",
      value: displayValue(currentParticipant?.participant_external_id),
    },
  ]
    .filter((pill) => pill.value !== "-") // Exclude pills with unavailable values
    .map((pill) => ({
      ...pill,
      copyable: true,
    }))

  // Shared helper for pill copy interactions.
  const copyPillValue = async ({ value, copiedKey, errorMessage }) => {
    await copyWithFeedback({
      value,
      invalidValues: ["-"],
      setFeedbackState: setCopiedPillKey,
      feedbackValue: copiedKey,
      resetValue: null,
      timerRef: copyResetTimerRef,
      errorMessage,
    })
  }

  return (
    <section className={styles.card}>
      {isRefreshing && (
        <div className={styles.loadingBarTop}>
          <LoaderProgressBar />
        </div>
      )}
      <section
        className={`${styles.panel} ${styles.panelAccent} ${styles.topCombinedPanel}`}
      >
        <div className={`${styles.panelProfile} ${styles.topCombinedRight}`}>
          <div className={styles.photoWrap}>
            <ProfilePhoto
              participant_profile_image={
                currentParticipant?.participant_profile_image
              }
            />
          </div>

          <div className={styles.identityBlock}>
            <h2 className={styles.participantName}>{displayValue(fullName)}</h2>
            {preferredParticipantName && (
              <p className={styles.preferredParticipantName}>
                {preferredParticipantName}
              </p>
            )}
            <div
              className={`${styles.idRow} ${
                preferredParticipantName ? styles.idRowWithPreferredName : ""
              }`}
            >
              {idPills.map((pill) => {
                const isCopied = copiedPillKey === pill.key
                const helperText = pill.copyable
                  ? isCopied
                    ? `${pill.label} copied`
                    : `Copy ${pill.label}`
                  : `${pill.label} unavailable`

                return (
                  <button
                    key={pill.key}
                    type="button"
                    className={`${styles.idChipButton} ${isCopied ? styles.idChipCopied : ""} ${
                      !pill.copyable ? styles.idChipStatic : ""
                    }`}
                    onClick={
                      pill.copyable
                        ? () =>
                            copyPillValue({
                              value: pill.value,
                              copiedKey: pill.key,
                              errorMessage: "Failed to copy ID value",
                            })
                        : undefined
                    }
                    title={helperText}
                    aria-label={helperText}
                  >
                    <span className={styles.idChipLabel}>{pill.label}:</span>{" "}
                    <span className={styles.idChipValue}>{pill.value}</span>
                  </button>
                )
              })}
            </div>
          </div>
        </div>

        <div className={styles.topDivider} />

        <div className={styles.topCombinedLeft}>
          <h3 className={`${styles.panelTitle} ${styles.engagementTitle}`}>
            Last Engagement
          </h3>
          <div className={styles.rowsWrap}>
            <div className={styles.infoLine}>
              <span className={styles.lineLabel}>Engagement:</span>
              <span className={styles.lineValue}>{lastActivity}</span>
            </div>
            <div className={styles.infoLine}>
              <span className={styles.lineLabel}>Date:</span>
              <span className={styles.lineValue}>{lastEngagementDate}</span>
            </div>
          </div>
        </div>
      </section>

      <div className={styles.bottomRow}>
        <section className={styles.bottomSection}>
          <h3 className={`${styles.panelTitle} ${styles.engagementTitle}`}>
            Assignments
          </h3>
          <div className={styles.rowsWrap}>
            {assignmentRows.map((row) => (
              <div
                key={row.label}
                className={styles.infoLine}
                title={`${row.label}: ${row.value}`}
              >
                <span className={styles.lineLabel}>{row.label}:</span>
                <span className={styles.lineValue}>{row.value}</span>
              </div>
            ))}
          </div>
        </section>

        <section
          className={`${styles.bottomSection} ${styles.bottomSectionRight}`}
        >
          <h3 className={`${styles.panelTitle} ${styles.engagementTitle}`}>
            Contact Details
          </h3>
          <div className={styles.rowsWrap}>
            {contactRows.map((row) => (
              <div
                key={row.label}
                className={styles.infoLine}
                title={`${row.label}: ${row.value}`}
              >
                <span className={styles.lineLabel}>{row.label}:</span>
                {row.pills && row.pills.length > 0 ?
                  <div className={styles.contactPillRow}>
                    {row.pills.map((pillValue, index) => {
                      const contactKey = `${row.label}-${index}-${pillValue}`
                      const isCopied = copiedPillKey === contactKey
                      const helperText = isCopied
                        ? `${row.label} copied`
                        : `Copy ${row.label}`

                      return (
                        <button
                          key={contactKey}
                          type="button"
                          className={`${styles.contactPillButton} ${
                            isCopied ? styles.contactPillCopied : ""
                          }`}
                          onClick={() =>
                            copyPillValue({
                              value: pillValue,
                              copiedKey: contactKey,
                              errorMessage: "Failed to copy contact value",
                            })
                          }
                          title={helperText}
                          aria-label={helperText}
                        >
                          {pillValue}
                        </button>
                      )
                    })}
                  </div>
                :
                  <span className={styles.lineValue}>{row.value}</span>
                }
              </div>
            ))}
          </div>
        </section>
      </div>
    </section>
  )
}
